import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, Platform, StyleSheet } from 'react-native';
import type { CalendarDay } from '../kpiService';

interface Props {
  data: CalendarDay[];
}

const DAY_HEADERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CELL = 18;
const GAP  = 2;

function parseDDMMYYYY(s: string): Date | null {
  const p = s.split('/');
  if (p.length !== 3) return null;
  return new Date(+p[2], +p[1] - 1, +p[0]);
}

export default function HeatmapCalendar({ data }: Props) {
  if (Platform.OS !== 'web') {
    return (
      <View style={styles.noData}>
        <Text style={styles.noDataText}>Calendar view available on web</Text>
      </View>
    );
  }

  if (!data.length) {
    return (
      <View style={styles.noData}>
        <Text style={styles.noDataText}>No monitoring records</Text>
      </View>
    );
  }

  // Group by YYYY-MM
  const byMonth: Record<string, { date: Date; hasBreach: boolean }[]> = {};
  data.forEach(d => {
    const parsed = parseDDMMYYYY(d.date);
    if (!parsed) return;
    const key = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
    if (!byMonth[key]) byMonth[key] = [];
    byMonth[key].push({ date: parsed, hasBreach: d.hasBreach });
  });

  const monthKeys = Object.keys(byMonth).sort();
  const lastIdx   = monthKeys.length - 1;

  const [idx, setIdx] = useState(lastIdx);

  // If data changes (e.g. filter), reset to latest month
  useEffect(() => { setIdx(monthKeys.length - 1); }, [monthKeys.length]);

  const safeIdx = Math.min(Math.max(0, idx), lastIdx);
  const mk      = monthKeys[safeIdx];
  const entries = byMonth[mk] ?? [];

  const firstOfMonth = new Date(entries[0]?.date.getFullYear() ?? 0, entries[0]?.date.getMonth() ?? 0, 1);
  const daysInMonth  = new Date(firstOfMonth.getFullYear(), firstOfMonth.getMonth() + 1, 0).getDate();
  const startDow     = firstOfMonth.getDay();
  const monthLabel   = `${MONTH_NAMES[firstOfMonth.getMonth()]} ${firstOfMonth.getFullYear()}`;

  const dayMap: Record<number, boolean | undefined> = {};
  entries.forEach(e => { dayMap[e.date.getDate()] = e.hasBreach; });

  const cells: (number | null)[] = [
    ...Array(startDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <View>
      {/* Month nav */}
      <View style={styles.navRow}>
        <TouchableOpacity
          onPress={() => setIdx(i => Math.max(0, i - 1))}
          disabled={safeIdx === 0}
          style={[styles.navBtn, safeIdx === 0 && styles.navBtnDisabled]}
        >
          <Text style={styles.navArrow}>‹</Text>
        </TouchableOpacity>

        <Text style={styles.monthLabel}>{monthLabel}</Text>

        <TouchableOpacity
          onPress={() => setIdx(i => Math.min(lastIdx, i + 1))}
          disabled={safeIdx === lastIdx}
          style={[styles.navBtn, safeIdx === lastIdx && styles.navBtnDisabled]}
        >
          <Text style={styles.navArrow}>›</Text>
        </TouchableOpacity>
      </View>

      {/* Day-of-week headers */}
      <View style={styles.dowRow}>
        {DAY_HEADERS.map((d, i) => (
          <View key={i} style={styles.cell}>
            <Text style={styles.dowText}>{d}</Text>
          </View>
        ))}
      </View>

      {/* Weeks */}
      {weeks.map((week, wi) => (
        <View key={wi} style={styles.weekRow}>
          {Array.from({ length: 7 }).map((_, di) => {
            const dayNum = week[di] ?? null;
            if (dayNum === null) return <View key={di} style={styles.cell} />;
            const hasBreach   = dayMap[dayNum];
            const isMonitored = hasBreach !== undefined;
            const bg = !isMonitored ? '#F3F4F6' : hasBreach ? '#FFCDD2' : '#C8E6C9';
            return (
              <View key={di} style={[styles.cell, styles.dayCell, { backgroundColor: bg }]}>
                <Text style={[styles.dayText, { color: !isMonitored ? '#CCC' : '#333' }]}>{dayNum}</Text>
              </View>
            );
          })}
        </View>
      ))}

      {/* Legend */}
      <View style={styles.legend}>
        {[
          { color: '#C8E6C9', label: 'Clear' },
          { color: '#FFCDD2', label: 'Breach' },
          { color: '#F3F4F6', label: 'No data' },
        ].map(item => (
          <View key={item.label} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: item.color }]} />
            <Text style={styles.legendText}>{item.label}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  navRow:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  navBtn:       { paddingHorizontal: 6, paddingVertical: 2 },
  navBtnDisabled: { opacity: 0.25 },
  navArrow:     { fontSize: 16, color: '#555', fontWeight: '700', lineHeight: 18 },
  monthLabel:   { fontSize: 11, fontWeight: '700', color: '#444' },
  dowRow:       { flexDirection: 'row', gap: GAP, marginBottom: GAP },
  weekRow:      { flexDirection: 'row', gap: GAP, marginBottom: GAP },
  cell:         { width: CELL, height: CELL, alignItems: 'center', justifyContent: 'center' },
  dayCell:      { borderRadius: 3 },
  dowText:      { fontSize: 8, color: '#AAA', fontWeight: '700' },
  dayText:      { fontSize: 8, fontWeight: '600' },
  legend:       { flexDirection: 'row', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' },
  legendItem:   { flexDirection: 'row', alignItems: 'center', gap: 4 },
  legendDot:    { width: 8, height: 8, borderRadius: 2 },
  legendText:   { fontSize: 9, color: '#888' },
  noData:       { paddingVertical: 8, alignItems: 'center' },
  noDataText:   { fontSize: 11, color: '#BBB' },
});
